import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { sectionsApi } from '../api/sections.api';
import type { Section, CreateSectionDto, UpdateSectionDto } from '../types';

export const useSections = () => {
  return useQuery<Section[]>({
    queryKey: ['sections'],
    queryFn: () => sectionsApi.getAll(),
  });
};

export const useSection = (id: number) => {
  return useQuery<Section>({
    queryKey: ['sections', id],
    queryFn: () => sectionsApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateSection = () => {
  const queryClient = useQueryClient();

  return useMutation<Section, Error, { data: CreateSectionDto; file?: File }>({
    mutationFn: ({ data, file }) => sectionsApi.create(data, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sections'] });
      queryClient.invalidateQueries({ queryKey: ['categories', 'grouped-by-section'] });
    },
  });
};

export const useUpdateSection = () => {
  const queryClient = useQueryClient();

  return useMutation<Section, Error, { id: number; data: UpdateSectionDto; file?: File }>({
    mutationFn: ({ id, data, file }) => sectionsApi.update(id, data, file),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sections'] });
      queryClient.invalidateQueries({ queryKey: ['sections', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['categories', 'grouped-by-section'] });
    },
  });
};

export const useHideSection = () => {
  const queryClient = useQueryClient();

  return useMutation<Section, Error, { id: number; isHidden: boolean }>({
    mutationFn: ({ id, isHidden }) => sectionsApi.setHidden(id, isHidden),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['sections'] });
      queryClient.invalidateQueries({ queryKey: ['sections', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['categories', 'grouped-by-section'] });
    },
  });
};

export const useDeleteSection = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (id) => sectionsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sections'] });
      queryClient.invalidateQueries({ queryKey: ['categories', 'grouped-by-section'] });
      queryClient.invalidateQueries({ queryKey: ['categories', 'without-sections'] });
      queryClient.invalidateQueries({ queryKey: ['home-tiles'] });
      notifications.show({
        title: 'Успешно',
        message: 'Секция успешно удалена',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        title: 'Ошибка',
        message: 'Ошибка при удалении секции',
        color: 'red',
      });
    },
  });
};

